import React, { useState, useEffect } from "react";

import { fetchUserContext } from "@origin-digital/event-dispatcher";
import { useFeatureFlag } from "@origin-digital/feature-flags";

import { useAppContext } from "../../AppContext";
import { LiveChatButton } from "./LiveChatButton";
import { BASE_PATH } from "../../helpers/router.helper";

const INTERNET_CATEGORY = "internet-products-and-services";
const BILLING_PATH = `${BASE_PATH}/billing-and-payments`;
export const MESSAGE_US_LABEL = "Message us";
export const LIVE_CHAT_LABEL = "Live chat";

interface OptionProps {
  category?: string;
  isAuthenticated: boolean;
  hasKrakenAccount: boolean;
  chatForInternet: boolean;
  chatForBilling: boolean;
  chatForBillingNativeOnly: boolean;
  isNativeApp: boolean;
}

const ALLOWED_KRAKEN_STATUSES = ["ACTIVE", "SETUP", "CLOSED"];

export const checkHasKrakenAccount = async (): Promise<boolean> => {
  const userContext = await fetchUserContext({});
  return (
    userContext?.accounts?.some(
      (account) =>
        account.backend === "KRAKEN" &&
        ALLOWED_KRAKEN_STATUSES.includes(account.status)
    ) ?? false
  );
};

const shouldShowChat = (options: OptionProps) => {
  const {
    category,
    isAuthenticated,
    hasKrakenAccount,
    chatForInternet,
    chatForBilling,
    chatForBillingNativeOnly,
    isNativeApp,
  } = options;

  const isInternet = category?.includes(INTERNET_CATEGORY);
  const isBilling = window?.location?.pathname === BILLING_PATH;
  const isBillingAndAuthed = isBilling && isAuthenticated && hasKrakenAccount;

  const showForInternet = chatForInternet && isInternet;
  const showForBilling = chatForBilling && isBillingAndAuthed;
  const showForBillingNative =
    chatForBillingNativeOnly && isBillingAndAuthed && isNativeApp;

  const shouldShow = showForInternet || showForBilling || showForBillingNative;
  const label = isBilling ? MESSAGE_US_LABEL : LIVE_CHAT_LABEL;

  return { shouldShow, label };
};

export const LiveChatSupport = ({ category }) => {
  const { isAuthenticated, isNative } = useAppContext();
  const [hasKraken, setHasKraken] = useState(false);

  const chatForInternet = useFeatureFlag("chatForInternet");
  const chatForBilling = useFeatureFlag("chatForBilling");
  const chatForBillingNativeOnly = useFeatureFlag("chatForBillingNativeOnly");

  useEffect(() => {
    if (isAuthenticated) {
      checkHasKrakenAccount().then(setHasKraken);
    }
  }, [isAuthenticated]);

  const { shouldShow, label } = shouldShowChat({
    category,
    isAuthenticated,
    hasKrakenAccount: hasKraken,
    chatForInternet,
    chatForBilling,
    chatForBillingNativeOnly,
    isNativeApp: isNative,
  });

  return shouldShow ? <LiveChatButton label={label} /> : null;
};
